import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BanknotesIcon,
  ShoppingBagIcon,
  BuildingStorefrontIcon,
  WrenchScrewdriverIcon,
  ReceiptPercentIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import axiosInstance from "@/lib/axios";

const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Number(n ?? 0));
const fcfa = (n) => `${fmt(Math.round(Number(n ?? 0)))} F`;
const dateFr = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "–";

// Presets de période (jours en arrière depuis aujourd'hui). null = tout l'historique.
const PERIODS = [
  { key: "7d", label: "7 jours", days: 7 },
  { key: "30d", label: "30 jours", days: 30 },
  { key: "90d", label: "90 jours", days: 90 },
  { key: "365d", label: "12 mois", days: 365 },
  { key: "all", label: "Tout", days: null },
];

const METHOD_LABEL = {
  orange: "Orange Money",
  moov: "Moov Money",
  sank: "Sank",
  telecel: "Telecel",
  cash: "Espèces",
  promotion: "Offert",
  autre: "Autre",
};
const methodLabel = (m) => METHOD_LABEL[m] || (m ? m.charAt(0).toUpperCase() + m.slice(1) : "Autre");

const toISO = (d) => d.toISOString().split("T")[0];

const StatTile = ({ icon, label, value, sub, accent = "gray" }) => {
  const ring = {
    gray: "bg-gray-50 text-gray-600",
    green: "bg-green-50 text-green-600",
    indigo: "bg-indigo-50 text-indigo-600",
    amber: "bg-amber-50 text-amber-600",
    sky: "bg-sky-50 text-sky-600",
  }[accent];
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${ring}`}>
          {React.createElement(icon, { className: "h-5 w-5" })}
        </div>
        {sub != null && <span className="text-xs text-gray-400">{sub}</span>}
      </div>
      <p className="mt-3 text-xl font-medium text-gray-800">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
};

const PaymentStatistics = () => {
  const [period, setPeriod] = useState("30d");

  const range = useMemo(() => {
    const preset = PERIODS.find((p) => p.key === period);
    if (!preset || preset.days == null) return { start_date: null, end_date: null };
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - preset.days);
    return { start_date: toISO(start), end_date: toISO(end) };
  }, [period]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["transactions-overview", range.start_date, range.end_date],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (range.start_date) params.append("start_date", range.start_date);
      if (range.end_date) params.append("end_date", range.end_date);
      const q = params.toString();
      const res = await axiosInstance.get(`/api/v1/admin/transactions/overview${q ? `?${q}` : ""}`);
      return res.data;
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return <div className="text-sm text-red-500 p-4">Erreur lors du chargement des transactions.</div>;

  const totals = data?.totals || {};
  const sources = data?.sources || {};
  const byMethod = data?.by_method || [];
  const revenue = Number(totals.revenue || 0);

  return (
    <div className="space-y-6">
      {/* En-tête + sélecteur de période */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-medium text-gray-800">Transactions</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Commandes et abonnements sur la période — {dateFr(data?.period?.start)} au {dateFr(data?.period?.end)}
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md cursor-pointer transition-colors ${
                period === p.key ? "bg-primary text-white" : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tuiles principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          icon={BanknotesIcon}
          label="Revenu total encaissé"
          value={fcfa(revenue)}
          accent="green"
        />
        <StatTile
          icon={ReceiptPercentIcon}
          label="Transactions"
          value={fmt(totals.transactions)}
          accent="sky"
        />
        <StatTile
          icon={ShoppingBagIcon}
          label="Panier moyen (commandes)"
          value={fcfa(totals.avg_order)}
          accent="indigo"
        />
        <StatTile
          icon={ClockIcon}
          label="Abonnements à valider"
          value={fmt(data?.pending_subscriptions)}
          accent="amber"
        />
      </div>

      {/* Ventilation par source de revenu */}
      <div>
        <h2 className="text-sm font-medium text-gray-700 mb-3">Par source de revenu</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SourceCard
            icon={ShoppingBagIcon}
            accent="indigo"
            label="Commandes"
            amount={sources.orders?.amount}
            count={sources.orders?.count}
            revenue={revenue}
          />
          <SourceCard
            icon={BuildingStorefrontIcon}
            accent="sky"
            label="Abonnements boutiques"
            amount={sources.shop_subscriptions?.amount}
            count={sources.shop_subscriptions?.count}
            revenue={revenue}
          />
          <SourceCard
            icon={WrenchScrewdriverIcon}
            accent="amber"
            label="Abonnements services"
            amount={sources.service_subscriptions?.amount}
            count={sources.service_subscriptions?.count}
            revenue={revenue}
          />
        </div>
      </div>

      {/* Ventilation par moyen de paiement */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-base font-medium text-gray-800">Par moyen de paiement</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Toutes sources confondues (commandes + abonnements).
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Moyen</th>
                <th className="px-4 py-3 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wide">Transactions</th>
                <th className="px-4 py-3 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wide">Montant</th>
                <th className="px-4 py-3 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wide">Part</th>
              </tr>
            </thead>
            <tbody>
              {byMethod.length > 0 ? (
                byMethod.map((m) => {
                  const share = revenue > 0 ? Math.round((Number(m.amount) / revenue) * 100) : 0;
                  return (
                    <tr key={m.method} className="border-b border-gray-100">
                      <td className="px-4 py-3 text-sm font-medium text-gray-800">{methodLabel(m.method)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{fmt(m.count)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-800 text-right">{fcfa(m.amount)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center gap-2">
                          <span className="hidden sm:block w-20 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                            <span className="block h-full bg-primary" style={{ width: `${share}%` }} />
                          </span>
                          <span className="text-xs text-gray-500 w-9 text-right">{share}%</span>
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-sm text-gray-500">
                    Aucune transaction sur cette période.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SourceCard = ({ icon, accent, label, amount, count, revenue }) => {
  const ring = {
    indigo: "bg-indigo-50 text-indigo-600",
    sky: "bg-sky-50 text-sky-600",
    amber: "bg-amber-50 text-amber-600",
  }[accent];
  const amt = Number(amount || 0);
  const share = revenue > 0 ? Math.round((amt / revenue) * 100) : 0;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${ring}`}>
          {React.createElement(icon, { className: "h-5 w-5" })}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-800">{label}</p>
          <p className="text-xs text-gray-500">{fmt(count)} transaction(s)</p>
        </div>
      </div>
      <p className="mt-3 text-lg font-medium text-gray-800">{fcfa(amt)}</p>
      <div className="mt-2 flex items-center gap-2">
        <span className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <span className="block h-full bg-primary" style={{ width: `${share}%` }} />
        </span>
        <span className="text-xs text-gray-500">{share}%</span>
      </div>
    </div>
  );
};

export default PaymentStatistics;
